<template>
  <div class="container-fluid">
    <page-header
      :page="header.page"
      :actions="header.actions"
      left-side-class="col-xl-8"
      right-side-class="col-xl-4 d-flex align-items-end justify-content-end pb-2"
    />

    <div v-if="showAdd" class="mt-4">
      <add @close="closeAdd" @continue="add" />
    </div>

    <filter-manager
      v-if="!showAdd"
      ref="filter"
      v-model="filter.model"
      :filters="filters"
      class="mt-4 vocational-referral-filter"
      search-container-class="vocational-referral-filter__search"
      @search="searchHandler"
      @status="onStatusChange"
      :display-advanced="false"
    />

    <template v-if="!showAdd">
      <div class="row my-4 mx-0">
        <div class="col-12">
          <custom-separator text="Resultados" />
        </div>
      </div>

      <custom-result-not-found
        v-if="showNotResults"
        class="mt-4 row pt-3"
        text="No hay Información Disponible."
      />

      <section v-else class="vocational-referral-results">
        <result-row
          v-for="item in results"
          :key="item.id"
          :item="item"
          @enter="goToManage"
        />

        <pagination
          v-if="pagination.totalItemCount > pagination.pageSize"
          :data="pagination"
          :page-size="pagination.pageSize"
          @pagination-go-page="onPageChange"
        />
      </section>
    </template>
  </div>
</template>

<script>
import storeMixin from '@/mixin/storeGenerator';
import alertMixin from '@/mixin/alert.mixin';
import objectToFormData from 'object-to-formdata';

import PageHeader from 'PageHeader';
import FilterManager from 'FilterManager';
import CustomSeparator from 'custom-separator';
import CustomResultNotFound from 'custom-result-not-found';
import Pagination from 'pagination';

import Add from './components/Add.vue';
import ResultRow from './components/ResultRow.vue';

import {
  getStatuses,
  save,
  search,
} from '@/services/vocational-rehabilitation-referral/api.js';

const PAGE_SIZE = 10;

const initialFilterModel = () => ({
  status: null,
  searchText: null,
});

const initialPagination = () => ({
  count: 0,
  currentPage: 1,
  pageCount: 1,
  totalItemCount: 0,
  pageSize: PAGE_SIZE,
});

export default {
  name: 'VocationalRehabilitationReferralMain',
  mixins: [storeMixin, alertMixin],
  components: {
    PageHeader,
    FilterManager,
    CustomSeparator,
    CustomResultNotFound,
    Pagination,
    Add,
    ResultRow,
  },
  data() {
    return {
      moduleName: 'VocationalRehabilitationReferralModule',
      showAdd: false,
      isLoading: false,
      filter: {
        model: initialFilterModel(),
        options: {
          statuses: [],
        },
      },
      results: [],
      pagination: initialPagination(),
    };
  },
  computed: {
    filters() {
      return [
        {
          name: 'status',
          component: 'CustomDropDown',
          options: this.filter.options.statuses,
          value: this.filter.model.status,
          placeholder: 'Estatus',
          fieldName: 'name',
          fieldKey: 'id',
          clearable: true,
          class: 'col-12 col-md-5 mb-2 mb-md-0',
          classContainer: 'w-100',
          mapParentValue: this.filter.model.status == null,
        },
        {
          name: 'searchText',
          component: 'CustomInput',
          value: this.filter.model.searchText,
          placeholder: 'Escriba Número de SIE o Número de Referido',
          icon: 'fa fa-search orange-icon',
          leftIcon: true,
          class: 'col-12 col-md-6 mb-2 mb-md-0',
          type: 'text',
        },
      ];
    },
    header() {
      return {
        page: {
          icon: 'fas fa-user',
          title: 'Referido a Rehabilitación Vocacional (ARV)',
          routes: [
            'Inicio',
            'PEI',
            'Referido a Rehabilitación Vocacional (ARV)',
          ],
        },
        actions: [
          {
            text: 'Regresar',
            icon: 'fas fa-arrow-circle-left',
            variant: 'outline-success',
            click: () => this.$router.back(),
          },
          {
            text: 'Añadir',
            icon: 'fas fa-file-plus',
            variant: 'success',
            visible: !this.showAdd,
            click: () => (this.showAdd = true),
          },
        ],
      };
    },
    showNotResults() {
      return !this.isLoading && this.results.length === 0;
    },
  },
  async mounted() {
    await this.loadStatuses();
  },
  destroyed() {
    this.persistData();
  },
  beforeRouteEnter(_, from, next) {
    const persistRoutes = ['vocational-rehabilitation-referral-manage'];

    next((vm) => {
      if (persistRoutes.includes(from.name)) {
        vm.recoveryData();
        vm.search(vm.filter.model, vm.pagination.currentPage);
      } else {
        vm.shouldRecovery = false;
        vm.filter.model = initialFilterModel();
        vm.pagination = initialPagination();
        vm.search(vm.filter.model);
      }
    });
  },
  methods: {
    closeAdd() {
      this.showAdd = false;
    },
    async add(payload) {
      this.closeAdd();

      try {
        const formData = objectToFormData(payload || {}, { indices: true });
        const { data } = await save(formData);
        if (!data?.id)
          throw new Error('No se obtuvo el id del registro guardado.');

        this.ShowToast(
          'Éxito',
          'El referido fue guardado como borrador.',
          'success'
        );
        this.$router.push({
          name: 'vocational-rehabilitation-referral-manage',
          params: { id: data.id },
        });
      } catch (error) {
        this.ShowToast(
          'Error',
          error?.response?.data?.message ||
            error?.message ||
            'No fue posible guardar el referido.',
          'error'
        );
      }
    },
    async loadStatuses() {
      try {
        const { data } = await getStatuses();
        this.filter.options.statuses = data;
      } catch (error) {
        this.ShowToast(
          'Error',
          error?.response?.data?.message ||
            error?.message ||
            'No fue posible cargar los estatus.',
          'error'
        );
      }
    },
    async search(filters, page = 1) {
      this.isLoading = true;

      try {
        const { data } = await search({
          statusId: filters.status?.id || null,
          searchText: filters.searchText || null,
          pageIndex: page,
          pageSize: PAGE_SIZE,
        });

        this.results = data.data || [];
        this.pagination = {
          ...this.pagination,
          totalItemCount: data.totalItemCount,
          pageCount: data.pageCount,
          currentPage: data.currentPage,
          count: data.count,
        };
      } catch (error) {
        this.ShowToast(
          'Error',
          error?.response?.data?.message ||
            error?.message ||
            'No fue posible cargar los referidos.',
          'error'
        );
      } finally {
        this.isLoading = false;
      }
    },
    async searchHandler(filters) {
      await this.search(filters);
    },
    async onPageChange(page) {
      await this.search(this.filter.model, page);
    },
    onStatusChange(value) {
      this.filter.model.status = value;
    },
    goToManage(id) {
      this.$router.push({
        name: 'vocational-rehabilitation-referral-manage',
        params: { id },
      });
    },
  },
};
</script>

<style scoped lang="scss">
::v-deep .custom-datetime-picker-container.width-container {
  width: 100% !important;
}

::v-deep .vocational-referral-filter {
  border-radius: 4px;

  .vocational-referral-filter__search {
    &.col-lg-3 {
      flex: 0 0 10%;
      max-width: 10%;
      padding-left: 0;
    }
  }
}

.vocational-referral-results {
  margin-bottom: 1.5rem;
}

@media (max-width: 991.98px) {
  ::v-deep .vocational-referral-filter {
    .vocational-referral-filter__search {
      &.col-lg-3 {
        flex: 0 0 100%;
        max-width: 100%;
        padding-left: 15px;
      }
    }
  }
}
</style>
